export const DEFAULT_STREAMING_RULES = {
  minScoreForPositiveOpenCv: 40,
  minScoreForPositiveFallback: 55,
  positiveMinRegionMotionDelta: 0.12,
  positiveMaxHomographyInlierRatio: 0.9,
  positiveMaxMotionMean: 500,  // STEP 10: raised from 200 — allow fast-sweep frames to be positive
  negativeMaxRegionMotionDelta: 0.1,
  invalidMinMotionMean: 500,   // STEP 10: raised from 300 — fast sweeps produce motionMean 400-600
  invalidMinResidualMean: 150, // STEP 11: lowered from 300 — real depth scenes max residual ~57 (test data); 284+ is excessive movement on flat
  invalidMinFrameSimilarityReject: 0.1,
  invalidMinGoodMatches: 8,
  passMinPositiveNoNegative: 3,
  passMinPositiveOverall: 2,
  // STEP 14: strengthened guards for strong_positive_depth_evidence path.
  // Prevents early pass with sparse positives (e.g. pos=2, tests=9, invalid=7 from dynamic screen).
  passStrongMinTotalTests: 12,   // min total tests — window must have collected enough evidence
  passStrongMinPositive: 5,      // min positive tests — must have clear majority of positives
  // STEP 14: DynamicScreenGuard — low-texture + high-motion + high-residual pattern
  // typical of dynamic video on screen. Overrides positive classification for such windows.
  dynamicScreenGuardMaxTextureScore: 0.08,
  dynamicScreenGuardMinMotionMean:    180,
  dynamicScreenGuardMinResidualMean:   80,
  failMinNegativeTests: 3,
  failMinScreenSignals: 3,
  grayZoneConsensusMinPositive: 8,
  grayZoneConsensusMinMeanScore: 72,
  grayZoneConsensusMaxStableScoreStdDev: 16,
  grayZoneConsensusMaxInvalidTests: 6,
  grayZoneMajorityTimeoutMinPositive: 9,
  grayZoneMajorityTimeoutMaxNegativeTests: 2,
  grayZoneMajorityTimeoutMaxInvalidTests: 10,
  grayZoneMajorityTimeoutMinMeanScore: 56,
  grayZoneMajorityTimeoutMaxStableScoreStdDev: 18,
  // STEP 8 — gray-zone dynamic screen veto
  // When hasDynamicWindow is true AND all positives come from the
  // ambiguous residual band [grayZoneResidualMin, grayZoneResidualMax],
  // block the easy passMinPositiveOverall (2) path and require the
  // stronger passMinPositiveNoNegative (3) threshold instead.
  grayZoneResidualMin: 1.5,  // lower edge of ambiguous band
  grayZoneResidualMax: 120.0, // STEP 13: raised from 50 — TV with dynamic content produces residuals 50–120 (escapes gray-zone veto at 50); real close scenes produce residuals >120
};

const safeNum = (v, fallback = 0) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

const computeStdDev = (values) => {
  if (!values.length) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
  return Math.sqrt(Math.max(0, variance));
};

const computeMean = (values) => {
  if (!values.length) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
};

const normalizeOutcome = (outcome) => String(outcome || "").toLowerCase();

const normalizeOutcomeLabel = (outcome) => {
  const normalized = String(outcome || "").trim().toLowerCase();
  if (normalized === "fake depth detected") return "FLAT SCENE DETECTED";
  if (normalized === "this is a live scene") return "DEPTH SCENE DETECTED";
  if (normalized === "further verification needed") return "UNCERTAIN";
  return outcome ?? null;
};

export default class StreamingDepthAggregator {
  constructor(rules = {}) {
    this.rules = { ...DEFAULT_STREAMING_RULES, ...rules };
    this.reset();
  }

  reset() {
    this.numberOfTests = 0;
    this.positiveDepthTests = 0;
    this.negativeDepthTests = 0;
    this.invalidTests = 0;
    this.screenLikeSignals = 0;
    this.scores = [];
    this.positiveScores = [];
    this.lastPayload = null;
    this.lastReasons = [];
    this.latestWindowClass = null;
    this.windows = [];
    this.hasCountedDualPhaseScreenPattern = false;
    this.latestResidualMean = null;
    this.latestMotionStdDev = null;
    this.latestGoodMatchCount = null;
    this.hasPlanarWindow = false;
    this.hasDynamicWindow = false;
    // STEP 8 — gray-zone tracking
    this.grayZonePositives = 0;
  }

  ingest(payload) {
    this.numberOfTests += 1;
    this.lastPayload = payload;

    const outcome = normalizeOutcome(payload?.outcome);
    const analysisData = payload?.analysisData || {};
    const score = safeNum(payload?.depthScore, -1);
    const isFallback = outcome === "fallback";
    const positiveThreshold = isFallback
      ? this.rules.minScoreForPositiveFallback
      : this.rules.minScoreForPositiveOpenCv;
    const motionMean = safeNum(analysisData?.motionMean, 0);
    const residualMean = safeNum(analysisData?.residualMean, 0);
    const planarityScore = safeNum(analysisData?.planarityScore, 0);

    const suspiciousDynamicScreen = analysisData?.suspiciousDynamicScreen === true;
    if (suspiciousDynamicScreen) {
      this.screenLikeSignals += 1;
    }

    const isPlanar = planarityScore > 0.9 && residualMean < 1;
    const isDynamicScreen =
      suspiciousDynamicScreen ||
      // STEP 10: raised from (>300 && >100) to (>600 && >200).
      // Fast legitimate sweeps produce motionMean 400-600 with high residual.
      // Only flag as dynamic screen for truly extreme values beyond normal sweep range.
      (motionMean > 600 && residualMean > 200);

    this.windows.push({
      isPlanar,
      isDynamicScreen,
      residualMean,
    });

    const windowClass = this.classifyWindow({
      payload,
      outcome,
      analysisData,
      score,
      positiveThreshold,
    });
    this.latestWindowClass = windowClass;

    // STEP 14: DynamicScreenGuard — intercepts positive windows that match the
    // low-texture + high-motion + high-residual pattern of dynamic video on a screen.
    // Fires only after classifyWindow() to avoid interfering with existing invalid/negative paths.
    let effectiveWindowClass = windowClass;
    const textureScore = safeNum(analysisData?.textureScore, 1.0); // default 1.0 = no concern
    if (
      windowClass === 'positive' &&
      payload?.captureQuality === 'good' &&
      textureScore < this.rules.dynamicScreenGuardMaxTextureScore &&
      motionMean  > this.rules.dynamicScreenGuardMinMotionMean &&
      residualMean > this.rules.dynamicScreenGuardMinResidualMean
    ) {
      console.log('[DeepReal][DynamicScreenGuard]', {
        pairId:          this.numberOfTests,
        textureScore,
        motionMean,
        residualMean,
        frameSimilarity: safeNum(analysisData?.frameSimilarity, 1),
        action:          'downgraded_positive_to_invalid_or_screen',
      });
      this.screenLikeSignals += 1;
      effectiveWindowClass = 'negative';
    }

    if (effectiveWindowClass === "positive") {
      this.positiveDepthTests += 1;
      this.positiveScores.push(score);
      // STEP 8 — track gray-zone positives
      const isGrayZone =
        residualMean >= this.rules.grayZoneResidualMin &&
        residualMean <= this.rules.grayZoneResidualMax;
      if (isGrayZone) this.grayZonePositives += 1;
    } else if (effectiveWindowClass === "negative") {
      this.negativeDepthTests += 1;
    } else {
      this.invalidTests += 1;
    }

    if (score >= 0) this.scores.push(score);

    const reasons = Array.isArray(analysisData?.reasons) ? [...analysisData.reasons] : [];
    const hasPlanarWindow = this.windows.some((w) => w.isPlanar);
    const hasDynamicWindow = this.windows.some((w) => w.isDynamicScreen);
    this.latestResidualMean = safeNum(analysisData?.residualMean, null);
    this.latestMotionStdDev = safeNum(analysisData?.motionStdDev, null);
    this.latestGoodMatchCount = safeNum(analysisData?.goodMatchCount, null);
    this.hasPlanarWindow = hasPlanarWindow;
    this.hasDynamicWindow = hasDynamicWindow;

    if (
      hasPlanarWindow &&
      hasDynamicWindow &&
      !this.hasCountedDualPhaseScreenPattern
    ) {
      this.screenLikeSignals += 1;
      this.negativeDepthTests += 1;
      reasons.push("dual_phase_screen_pattern");
      this.hasCountedDualPhaseScreenPattern = true;
    }

    console.log("🧠 [Aggregator] Dual-phase detection:", {
      hasPlanarWindow,
      hasDynamicWindow,
      screenLikeSignals: this.screenLikeSignals,
      negativeDepthTests: this.negativeDepthTests,
    });

    this.lastReasons = reasons;

    return this.getSnapshot();
  }

  evaluate({ timeout = false } = {}) {
    const stableScoreStdDev = computeStdDev(this.positiveScores);
    const positiveScoreMean = computeMean(this.positiveScores);
    const passNoNegative =
      this.positiveDepthTests >= this.rules.passMinPositiveNoNegative &&
      this.negativeDepthTests === 0;
    const passStrongOverall =
      this.positiveDepthTests >= this.rules.passMinPositiveOverall &&
      // STEP 14: additional guards — prevents pos=2/tests=9/invalid=7 from passing early
      this.numberOfTests          >= this.rules.passStrongMinTotalTests &&
      this.positiveDepthTests     >= this.rules.passStrongMinPositive &&
      this.positiveDepthTests     >= this.invalidTests &&
      this.negativeDepthTests     <= 1;

    // STEP 8 — gray-zone ambiguity veto (v4 — STEP 12)
    //
    // Se tutti i positivi provengono dalla banda ambigua [grayZoneResidualMin, grayZoneResidualMax],
    // il PASS viene bloccato SEMPRE, indipendentemente dal conteggio e da hasDynamicWindow.
    //
    // Motivazione empirica (test dataset):
    // Tutti i PASS legittimi (T11,T13,T27) hanno almeno un positivo con
    // residualMean > grayZoneResidualMax (50) — cioè allPositivesAreGrayZone=false.
    // Una superficie piatta (copertina libro T32: 3 gray-zone positivi) non può mai
    // produrre un positivo fuori dalla gray zone perché non ha vera parallasse.
    //
    // Versione precedente (v3) aveva escape hatch:
    //   (hasDynamicWindow || positiveDepthTests < passMinPositiveNoNegative)
    // Con 3 positivi tutti gray-zone e hasDynamicWindow=false, il veto non scattava
    // e la copertina libro ha ottenuto PASS (T32 — falso positivo critico).
    const allPositivesAreGrayZone =
      this.grayZonePositives > 0 &&
      this.grayZonePositives === this.positiveDepthTests;
    const dynamicGrayZoneVeto = allPositivesAreGrayZone;
    const grayZoneConsensusTimeoutPass =
      timeout &&
      allPositivesAreGrayZone &&
      this.positiveDepthTests >= this.rules.grayZoneConsensusMinPositive &&
      this.negativeDepthTests === 0 &&
      this.screenLikeSignals === 0 &&
      this.invalidTests <= this.rules.grayZoneConsensusMaxInvalidTests &&
      positiveScoreMean >= this.rules.grayZoneConsensusMinMeanScore &&
      stableScoreStdDev <= this.rules.grayZoneConsensusMaxStableScoreStdDev;
    const grayZoneMajorityTimeoutPass =
      timeout &&
      allPositivesAreGrayZone &&
      this.positiveDepthTests >= this.rules.grayZoneMajorityTimeoutMinPositive &&
      this.negativeDepthTests <= this.rules.grayZoneMajorityTimeoutMaxNegativeTests &&
      this.invalidTests <= this.rules.grayZoneMajorityTimeoutMaxInvalidTests &&
      this.screenLikeSignals === 0 &&
      !this.hasDynamicWindow &&
      positiveScoreMean >= this.rules.grayZoneMajorityTimeoutMinMeanScore &&
      stableScoreStdDev <= this.rules.grayZoneMajorityTimeoutMaxStableScoreStdDev;

    if ((passNoNegative || passStrongOverall) && !dynamicGrayZoneVeto) {
      const confidence = Math.min(
        99,
        55 +
          this.positiveDepthTests * 8 -
          this.negativeDepthTests * 6 -
          this.screenLikeSignals * 5 +
          Math.max(0, 14 - stableScoreStdDev)
      );
      return {
        status: "pass",
        reason: passStrongOverall ? "strong_positive_depth_evidence" : "positive_without_negative",
        confidence: Math.round(confidence),
        snapshot: this.getSnapshot(),
      };
    }

    if (grayZoneConsensusTimeoutPass) {
      const confidence = Math.min(
        88,
        58 +
          this.positiveDepthTests * 2 -
          this.invalidTests * 2 +
          Math.max(0, 12 - stableScoreStdDev)
      );
      return {
        status: "pass",
        reason: "gray_zone_positive_consensus_timeout",
        confidence: Math.round(confidence),
        snapshot: this.getSnapshot(),
      };
    }

    if (grayZoneMajorityTimeoutPass) {
      const confidence = Math.min(
        82,
        52 +
          this.positiveDepthTests * 2 -
          this.negativeDepthTests * 4 -
          this.invalidTests +
          Math.max(0, 10 - stableScoreStdDev)
      );
      return {
        status: "pass",
        reason: "gray_zone_majority_timeout",
        confidence: Math.round(confidence),
        snapshot: this.getSnapshot(),
      };
    }

    if (
      this.negativeDepthTests >= this.rules.failMinNegativeTests ||
      this.screenLikeSignals >= this.rules.failMinScreenSignals
    ) {
      return {
        status: "fail",
        reason:
          this.screenLikeSignals >= this.rules.failMinScreenSignals
            ? "repeated_screen_like_motion"
            : "repeated_negative_depth_windows",
        confidence: 90,
        snapshot: this.getSnapshot(),
      };
    }

    if (timeout) {
      // STEP 11 — RIMOSSO (era gray-zone-only timeout → FAIL)
      // Rimosso perché il discriminatore gray-zone-only non è affidabile:
      // sia flat scene con movimento moderato che depth scene con piccolo movimento
      // producono 2 positivi gray-zone + 0 negativi + sessionCalm.
      // Il falso negativo su depth (T20: residual=5.92, grayZone=2) è più grave
      // del falso positivo su flat (T2: RETRY invece di FAIL al primo tentativo).
      // Le flat scene raggiungono FAIL al retry via 3 negativi o STEP 11b.

      // STEP 11b — planar-confirmed timeout → FAIL
      // Se hasPlanarWindow è true (fit omografico stretto su almeno una finestra con residual < 1),
      // c'è almeno 1 test negativo, nessun positivo e nessun gray-zone positive:
      // il timeout è evidenza sufficiente di scena piatta — il sistema ha visto abbastanza
      // per escludere la profondità senza aver raggiunto la soglia normale di 3 negativi.
      if (
        this.hasPlanarWindow &&
        this.negativeDepthTests >= 1 &&
        this.positiveDepthTests === 0 &&
        this.grayZonePositives === 0
      ) {
        return {
          status: "fail",
          reason: "planar_window_confirmed_timeout",
          confidence: 70,
          snapshot: this.getSnapshot(),
        };
      }

      return {
        status: "retry",
        reason: "timeout_with_insufficient_valid_windows",
        confidence: 45,
        snapshot: this.getSnapshot(),
      };
    }

    return {
      status: "continue",
      reason: "collecting_evidence",
      confidence: 0,
      snapshot: this.getSnapshot(),
    };
  }

  getSnapshot() {
    return {
      numberOfTests: this.numberOfTests,
      positiveDepthTests: this.positiveDepthTests,
      negativeDepthTests: this.negativeDepthTests,
      invalidTests: this.invalidTests,
      screenLikeSignals: this.screenLikeSignals,
      stableScoreStdDev: computeStdDev(this.positiveScores),
      latestDepthScore: safeNum(this.lastPayload?.depthScore, null),
      latestOutcome: normalizeOutcomeLabel(this.lastPayload?.outcome),
      latestWindowClass: this.latestWindowClass ?? null,
      latestReasons: this.lastReasons,
      residualMean: this.latestResidualMean,
      motionStdDev: this.latestMotionStdDev,
      goodMatchCount: this.latestGoodMatchCount,
      hasPlanarWindow: this.hasPlanarWindow,
      hasDynamicWindow: this.hasDynamicWindow,
      grayZonePositives: this.grayZonePositives, // STEP 8
    };
  }

  classifyWindow({ payload, outcome, analysisData, score, positiveThreshold }) {
    const isLowQuality = payload?.captureQuality === "insufficient";
    const motionMean = safeNum(analysisData?.motionMean, 0);
    const residualMean = safeNum(analysisData?.residualMean, 0);
    const frameSimilarity = safeNum(analysisData?.frameSimilarity, 1);
    const goodMatchCount = safeNum(analysisData?.goodMatchCount, 0);
    const regionMotionDelta = safeNum(analysisData?.regionMotionDelta, 0);
    const homographyInlierRatio = safeNum(analysisData?.homographyInlierRatio, 1);
    const suspiciousDynamicScreen = analysisData?.suspiciousDynamicScreen === true;
    const reasons = Array.isArray(analysisData?.reasons) ? analysisData.reasons : [];
    const explicitTooFewMatches = reasons.includes("too_few_matches");
    const hardScreenLikeWindow = suspiciousDynamicScreen;

    if (hardScreenLikeWindow) {
      return "negative";
    }

    const invalidWindow =
      motionMean > this.rules.invalidMinMotionMean ||
      residualMean > this.rules.invalidMinResidualMean ||
      frameSimilarity < this.rules.invalidMinFrameSimilarityReject ||
      explicitTooFewMatches ||
      goodMatchCount < this.rules.invalidMinGoodMatches;

    if (invalidWindow) {
      return "invalid";
    }

    const positiveWindow =
      !isLowQuality &&
      score >= positiveThreshold &&
      regionMotionDelta >= this.rules.positiveMinRegionMotionDelta &&
      homographyInlierRatio <= this.rules.positiveMaxHomographyInlierRatio &&
      motionMean < this.rules.positiveMaxMotionMean;

    if (positiveWindow) {
      return "positive";
    }

    const negativeWindow =
      (score < positiveThreshold &&
        regionMotionDelta < this.rules.negativeMaxRegionMotionDelta);

    return negativeWindow ? "negative" : "invalid";
  }
}
